import config from '../config';

class WaiversApi {
  constructor() {
    this.apiUrl = config.API_URL;
  }

  // Get current user ID from localStorage (deprecated)
  getCurrentUserId() {
    console.warn('getCurrentUserId() is deprecated. Pass currentUser as parameter instead.');
    return null;
  }

  // Generic request helper
  async makeRequest(endpoint, options = {}, currentUser = null) {
    const userId = currentUser?.id || this.getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    const url = new URL(`${this.apiUrl}${endpoint}`);
    url.searchParams.append('user_id', userId);

    const response = await fetch(url.toString(), {
      credentials: 'include',
      ...options
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  // Team Roster Methods
  async getTeamRoster(leagueId, teamName, currentUser = null) {
    try {
      const response = await this.makeRequest(`/leagues/${leagueId}/rosters?team_name=${encodeURIComponent(teamName)}`, {}, currentUser);
      return response.rosters?.[0]?.roster_data || [];
    } catch (error) {
      console.error('Error fetching team roster:', error);
      return [];
    }
  }

  async updateTeamRoster(leagueId, teamName, rosterData, currentUser = null) {
    return this.makeRequest(`/leagues/${leagueId}/rosters`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        team_name: teamName,
        roster_data: rosterData
      })
    }, currentUser);
  }

  // Get all team rosters for free agent detection
  async getAllTeamRosters(leagueId, teamNames, currentUser = null) {
    try {
      const rosters = {};
      for (const teamName of teamNames) {
        const rosterData = await this.getTeamRoster(leagueId, teamName, currentUser);
        rosters[teamName] = rosterData;
      }
      return rosters;
    } catch (error) {
      console.error('Error fetching all team rosters:', error);
      return {};
    }
  }

  // Waiver Transaction Methods (status: omit = all, 'processed' = history only, 'pending' = pending only)
  async getWaiverHistory(leagueId, teamName = null, currentUser = null, status = 'processed') {
    try {
      const userId = currentUser?.id || this.getCurrentUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }

      let queryString = `user_id=${userId}`;
      if (teamName) {
        queryString += `&team_name=${encodeURIComponent(teamName)}`;
      }
      if (status) {
        queryString += `&status=${encodeURIComponent(status)}`;
      }

      const response = await fetch(`${this.apiUrl}/leagues/${leagueId}/waiver-transactions?${queryString}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const transactions = data.transactions || [];

      // Transform backend format to UI format
      return transactions.map(tx => {
        let addPlayer = tx.player_name || '';
        let dropPlayer = tx.drop_player_name || null;
        if (!dropPlayer && tx.description) {
          const dropMatch = tx.description.match(/,\s*dropped\s+(.+)$/i);
          if (dropMatch) dropPlayer = dropMatch[1].trim();
        }
        return {
          id: tx.id,
          timestamp: tx.created_at,
          teamName: tx.team_name || '',
          addPlayer,
          dropPlayer,
          status: tx.status === 'success' ? 'success' : 'success',
          result: dropPlayer ? 'added' : 'added',
        };
      });
    } catch (error) {
      console.error('Error fetching waiver history:', error);
      return [];
    }
  }

  async addWaiverTransaction(leagueId, teamName, transactionData, currentUser = null) {
    try {
      const userId = currentUser?.id || this.getCurrentUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }

      const transaction = {
        user_id: userId,
        team_name: teamName,
        transaction_type: 'claim', // "add", "drop", or "claim"
        player_name: transactionData.addPlayer,
        player_id: String(transactionData.addPlayerId || transactionData.addPlayer),
        description: `Waiver: Added ${transactionData.addPlayer}${transactionData.dropPlayer ? `, dropped ${transactionData.dropPlayer}` : ''}`
      };

      const response = await fetch(`${this.apiUrl}/leagues/${leagueId}/waiver-transactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(transaction)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error adding waiver transaction:', error);
      throw error;
    }
  }

  // Helper methods for backward compatibility
  async getTeamWaiverHistory(teamName, leagueId, currentUser = null) {
    return this.getWaiverHistory(leagueId, teamName, currentUser);
  }

  async addWaiverTransactionLegacy(teamName, transactionData, leagueId, currentUser = null) {
    return this.addWaiverTransaction(leagueId, teamName, transactionData, currentUser);
  }

  // Submit a pending waiver claim (when rosters are locked); processed at waiver run
  async submitPendingClaim(leagueId, teamName, addPlayer, dropPlayerId, dropPlayerName, currentUser = null) {
    const userId = currentUser?.id || this.getCurrentUserId();
    if (!userId) throw new Error('User not authenticated');
    const response = await fetch(`${this.apiUrl}/leagues/${leagueId}/pending-waiver-claims`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        user_id: userId,
        team_name: teamName,
        add_player_id: String(addPlayer.player_id),
        add_player_name: addPlayer.player_name,
        add_player_position: addPlayer.position || 'WR',
        drop_player_id: dropPlayerId ? String(dropPlayerId) : null,
        drop_player_name: dropPlayerName || null,
      }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || response.statusText);
    }
    return response.json();
  }

  // Player IDs not available for FCFS (pending claims + post-drop waivers)
  async getUnavailableFreeAgents(leagueId, currentUser = null) {
    return this.makeRequest(`/leagues/${leagueId}/unavailable-free-agents`, {}, currentUser);
  }

  async getWaiverPriorities(leagueId, currentUser = null) {
    const data = await this.makeRequest(`/leagues/${leagueId}/waiver-priorities`, {}, currentUser);
    return data.priorities || [];
  }

  async getPendingClaims(leagueId, currentUser = null) {
    const userId = currentUser?.id || this.getCurrentUserId();
    if (!userId) throw new Error('User not authenticated');
    const url = `${this.apiUrl}/leagues/${leagueId}/waiver-transactions?user_id=${userId}&status=pending`;
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) throw new Error('Failed to fetch pending claims');
    const data = await response.json();
    return (data.transactions || []).map(tx => ({
      id: tx.id,
      teamName: tx.team_name,
      addPlayer: tx.player_name,
      player_id: tx.player_id,
      dropPlayer: tx.drop_player_name || null,
      createdAt: tx.created_at,
    }));
  }

  // Run waivers (commissioner only)
  async runWaivers(leagueId, currentUser = null) {
    return this.makeRequest(`/leagues/${leagueId}/run-waivers`, {
      method: 'POST',
    }, currentUser);
  }

  // Process waiver claim atomically on the server
  async processWaiverClaim(leagueId, teamName, addPlayer, dropPlayerId, currentUser = null) {
    const userId = currentUser?.id || this.getCurrentUserId();
    if (!userId) throw new Error('User not authenticated');

    const dropPlayerObj = dropPlayerId
      ? (await this.getTeamRoster(leagueId, teamName, currentUser)).find(
          (p) => String(p.player_id) === String(dropPlayerId)
        )
      : null;

    const response = await fetch(`${this.apiUrl}/leagues/${leagueId}/waiver-claims/process`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        user_id: userId,
        team_name: teamName,
        add_player_id: String(addPlayer.player_id),
        add_player_name: addPlayer.player_name,
        add_player_position: addPlayer.position || 'WR',
        drop_player_id: dropPlayerId ? String(dropPlayerId) : null,
        drop_player_name: dropPlayerObj?.player_name || null,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const err = new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      err.status = response.status;
      throw err;
    }

    const data = await response.json();
    return data.roster_data || [];
  }

  // Get free agents (players not on any roster)
  async getFreeAgents(leagueId, teamNames, allPlayers, currentUser = null) {
    try {
      const allRosters = await this.getAllTeamRosters(leagueId, teamNames, currentUser);
      const allRosteredPlayers = Object.values(allRosters).flat();
      const rosteredIds = new Set(allRosteredPlayers.map(p => p.player_id));
      
      return allPlayers.filter(p => !rosteredIds.has(p.player_id));
    } catch (error) {
      console.error('Error getting free agents:', error);
      return [];
    }
  }

  async dropPlayerToFreeAgency(leagueId, teamName, player, currentUser = null) {
    const userId = currentUser?.id || this.getCurrentUserId();
    if (!userId) throw new Error('User not authenticated');
    const response = await fetch(`${this.apiUrl}/leagues/${leagueId}/rosters/drop`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        user_id: userId,
        team_name: teamName,
        player_id: String(player.player_id || player.id),
        player_name: player.player_name || player.name || null,
      }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      const detail = err.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg || d).join('; ')
            : response.statusText;
      throw new Error(message || 'Failed to drop player');
    }
    return response.json();
  }
}

const waiversApi = new WaiversApi();
export default waiversApi;
